/**
 * Session Cookie Refresh Utility
 *
 * Handles refreshing expired Firebase session cookies by calling the sign-in endpoint
 * with a fresh ID token. This maintains server-side session continuity.
 */

import { getCurrentUserToken } from '../services/auth';
import { adminApiService } from '../services/adminApi';
import { readImpersonatorClaim } from './impersonationClaims';
import { createClientLogger } from './logger';

const logger = createClientLogger('SessionRefresh');

/**
 * Does this error mean "the server session cookie needs re-minting"?
 *
 * Gates the apiClient 401 interceptor's cookie-refresh branch. A false here
 * sends the interceptor down the bearer-only path, which cannot repair a
 * server-side cookie — so anything this misses is a permanent 401 storm.
 *
 * ⚠️ THE MISSING COOKIE IS THE COMMON CASE, NOT THE EXPIRED ONE. Every pattern
 * below except the last describes a cookie that was SENT and REJECTED. A real
 * browser almost never does that: once `Max-Age` passes it DELETES the cookie,
 * so the request arrives bare and the API answers "missing", never "expired".
 * For a year this function tested only for states the browser makes unreachable
 * and returned false for the one it actually produces — so `refreshSessionCookie`
 * never ran, and a session that died of old age could only be fixed by signing
 * out in another app (prod 2026-09-09: a live owner locked out of the workspace
 * chooser for ~30 minutes and told he had no tenants).
 */
export function isSessionExpiredError(error: any): boolean {
    const data = error?.response?.data;
    if (!data) return false;

    // ⚠️ TWO WIRE SHAPES, AND PRODUCTION SENDS THE ONE THIS USED TO IGNORE.
    //
    //   app envelope   {success:false, error:{code, message}, meta}
    //   Fastify default{statusCode, error:"Unauthorized", message}
    //
    // `apps/api` registers its `setErrorHandler` AFTER every route
    // (`server/server.ts`, under a comment saying it must be registered last),
    // and a Fastify child context inherits its error handler AT CREATION — so
    // the envelope reaches NO route. Verified on deployed main 2026-09-09: a
    // 404 carries `error.code` because `setNotFoundHandler` fires on the ROOT,
    // while every route error comes back in the default shape.
    //
    // In that shape `data.error` is the STRING "Unauthorized", so reading
    // `data.error.code` and `data.error.message` yields undefined twice — which
    // is how #1870 shipped, deployed, and did nothing. Read BOTH shapes: the
    // envelope is still the intended contract, and a route-level 4xx schema can
    // produce the default shape even after the registration order is fixed.
    const envelope =
        data.error && typeof data.error === 'object'
            ? (data.error as { code?: unknown; message?: unknown })
            : undefined;

    const errorMessage = String(envelope?.message ?? data.message ?? '');
    const errorCode = String(
        envelope?.code ??
            // Fastify puts its own code at the TOP level (e.g. FST_ERR_VALIDATION);
            // `data.error` there is a human status label, never a code, so it is
            // deliberately not consulted as one.
            data.code ??
            '',
    );

    return (
        errorMessage.includes('session cookie has expired') ||
        errorCode === 'auth/session-cookie-expired' ||
        errorMessage.includes('auth/session-cookie-expired') ||
        // The API's canonical session-invalid envelope code, emitted by the
        // global error handler when `SessionInvalidError` is thrown by the
        // auth plugin (e.g., session cookie revoked, Firebase user
        // deleted, refresh tokens revoked). Matching it here lets the
        // apiClient's existing cookie-refresh branch fire — without this,
        // it falls through to the bearer-only token refresh which can't
        // repair a dead server-side session cookie.
        errorCode === 'AUTH_SESSION_INVALID' ||
        // No credential reached the API at all — an aged-out cookie the browser
        // already discarded. Recoverable WITHOUT re-authentication whenever the
        // Firebase SDK is still signed in, which it is by construction: its
        // refresh token does not expire while the cookie tops out at 24h
        // (14d with rememberMe), so the two diverge on every long-lived login.
        errorCode === 'AUTH_SESSION_MISSING' ||
        // Same 401 from an API that has not shipped the code yet. `api` and the
        // web apps deploy independently, so the client must repair itself
        // against BOTH envelopes or this fix is dark until the API catches up.
        // Substring, not equality: the API prefixes nothing today, and a
        // prefix later must not silently un-fix this.
        errorMessage.includes('Missing session cookie or authorization header')
    );
}

/**
 * Did `/auth/signin` mint the cookie and then report only that the `users`
 * profile is absent? The apiClient interceptor rejects with an Error carrying
 * the envelope's `code` and the HTTP `status`.
 */
function isProfileMissingAfterMint(error: unknown): boolean {
    const e = error as { code?: unknown; status?: unknown } | null;
    return e?.code === 'USER_PROFILE_NOT_FOUND' && e?.status === 404;
}

/**
 * Refreshes the session cookie by calling the sign-in endpoint with a fresh ID token.
 * This creates a new server-side session cookie.
 * Uses the established adminApiService to respect existing architecture.
 *
 * @returns true if session was refreshed successfully, false otherwise
 */
export async function refreshSessionCookie(): Promise<boolean> {
    try {
        logger.debug('Session cookie expired, refreshing...');

        // Get a fresh ID token from Firebase
        const freshToken = await getCurrentUserToken(true);

        if (!freshToken) {
            logger.error('No fresh token available');
            return false;
        }

        // An impersonation / support-scope session must not be exchanged for a
        // session cookie: the cookie is shared across `.letsorderup.com` and
        // outlives `impersonator.exp` by days, so the scope would survive its
        // own expiry as a credential every app rejects. The API refuses this
        // too (`IMPERSONATION_SESSION_COOKIE_REFUSED`); returning early keeps
        // the keep-alive from clobbering the admin's real cookie on the way to
        // finding that out, and keeps the failure out of the error path.
        //
        // Deliberately ANY impersonator claim, not just a live one (unlike
        // `hasLiveImpersonatorClaim`, which gates whether to ACT on the scope):
        // minting a cookie from an expired scope produces a credential the API
        // rejects on sight and never clears. Returning false here is also what
        // routes an expired session into AuthGuard's sign-out path.
        if (readImpersonatorClaim(freshToken.claims) !== null) {
            logger.debug('Skipping session cookie re-mint for an impersonation session');
            return false;
        }

        // Use existing adminApiService to create a new session cookie
        // This respects all existing interceptors, error handling, and architecture
        await adminApiService.signInWithIdToken(freshToken.token, true);

        logger.debug('✅ Session cookie refreshed successfully');
        return true;
    } catch (error) {
        // ⚠️ A MISSING PROFILE IS NOT A DEAD SESSION. `/auth/signin` sets the
        // session cookie BEFORE it looks up the `users` profile, and answers
        // 404 `USER_PROFILE_NOT_FOUND` with the cookie already on the response
        // (`apps/api/components/routes/auth.ts`). Online-ordering guests who
        // add a phone for loyalty stop being anonymous but never get a profile,
        // so every re-mint for them landed here — and three of those raised
        // "Your session has expired" at checkout over a session that was fine
        // (prod 2026-09-29: 39 such 404s in 72h, orders still went through).
        if (isProfileMissingAfterMint(error)) {
            logger.warn('Session cookie re-minted; this account has no users profile');
            return true;
        }

        logger.error('❌ Error refreshing session cookie', { error: error instanceof Error ? error.message : String(error) });
        return false;
    }
}

